/**
 * Result rendering (§9.6). Everything is built with createElement and
 * textContent — no user-controlled HTML is ever rendered (§3.8). Copy
 * states what the rules ask for; it never advises, never promises an
 * outcome, and never uses red — amber for anything needing attention.
 */
import type { Determination, OutOfScopeFlag, RequirementResult } from "@verity/engine";
import { CHECK_LIMITS, financialWorking } from "@verity/engine";
import { pathwayFor } from "@verity/pathways";
import type { CheckContext } from "./revisit.js";

/**
 * Whether the adviser offer is shown. Injected at BUILD time, default off.
 *
 * Two reasons it is not a runtime setting. §9.1 guarantees the calculator makes
 * no network request while answering, proven by a Playwright test — fetching a
 * flag would break the property the whole page is built on. And this is a
 * regulated offer: offering immigration advice services is precisely what the
 * IAA licence permits, so the safe default has to be "absent from the bundle"
 * rather than "present but hidden".
 *
 * Turned on by building with ADVISER_OFFER=on. Matt turns it on; nothing in the
 * code decides that (ADR-0043).
 */
declare const __ADVISER_OFFER__: boolean;

/**
 * Read DIRECTLY, with no `typeof` guard. That guard was the first version and
 * it quietly defeated the whole point: esbuild cannot fold
 * `typeof X !== "undefined" && X === true` to a constant, so the branch — and
 * the offer copy inside it — survived minification and shipped to every
 * visitor in a build where the offer is off. Not shown, but readable in
 * view-source, which is not what "absent from the bundle" means.
 *
 * It also has to be read AT the guard, not through a helper: esbuild does not
 * eliminate across a function boundary, so `if (!enabled()) return null` kept
 * the body alive too. `if (!__ADVISER_OFFER__) return null` folds to
 * `if (true) return null` and everything after it becomes unreachable.
 *
 * build.mjs always supplies the define, so there is nothing to guard against.
 * `check-offer-stripped.mjs` proves the copy is gone rather than trusting it.
 */
import type { Ruleset } from "@verity/rules";

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

export const STANDING_LINE =
  "This is general information and it is the same for everyone who enters these figures. It is not advice about your application.";

/** The rules that can carry the financial verdict, in display order. */
const VERDICT_CARRIERS = [
  "FIN.INCOME.CAT-A",
  "FIN.INCOME.CAT-B",
  "FIN.SAVINGS.EC-LTR",
  "FIN.SAVINGS.ILR",
  "FIN.COMBINE.EC-LTR",
  "FIN.COMBINE.ILR",
];

/**
 * Requirements are grouped so the result reads as a picture of the whole
 * application rather than a flat list (ADR-0022 — the wizard now asks about
 * every group, not just the money). Order is deliberate: the gate on the
 * route first, the money second because it is the most consequential
 * calculation, then the rest.
 */
const GROUPS: Array<{ title: string; prefix: string }> = [
  { title: "Your partner", prefix: "SPN." },
  { title: "Money", prefix: "FIN." },
  { title: "Your relationship", prefix: "REL." },
  { title: "English language", prefix: "ENG." },
  { title: "Where you will live", prefix: "ACC." },
  { title: "Life in the UK", prefix: "KOLL." },
  { title: "Documents and formats", prefix: "EVD." },
];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(Date.UTC(y as number, (m as number) - 1, d as number)));
}

/**
 * A routed-out result.
 *
 * This used to say the same thing nine different ways: "outside what Verity
 * covers", plus a referral to gov.uk. That treated a case with a child on it —
 * an ordinary application the engine simply does not encode yet — exactly like
 * one where the sponsor cannot sponsor at all, which is a genuine no.
 *
 * `pathwayFor` decides which of the three it is, and the heading, explanation
 * and single action come from there. The ruleset's own `reason` strings are
 * still shown, because they say WHICH thing routed the case out; the pathway
 * says what to do about it.
 */
export function renderOutOfScope(
  container: HTMLElement,
  reasons: string[],
  referralHint: string,
  flags: readonly OutOfScopeFlag[] = [],
): void {
  container.replaceChildren();
  const pathway = pathwayFor(flags);

  const card = el("section", "result-card out-of-scope");
  card.append(
    el("h2", "verdict-line", pathway?.heading ?? "This is outside what Verity covers"),
  );
  for (const reason of reasons) card.append(el("p", undefined, reason));

  // The ruleset's own referral copy stays, whatever the pathway. It is
  // adviser-authored, case-specific, and in places says more than the pathway
  // ever could.
  card.append(el("p", "referral", referralHint));

  if (pathway) {
    card.append(el("p", "pathway-explanation", pathway.explanation));

    if (pathway.action.kind === "adviser_enquiry") {
      // Not a link yet — there is nobody to enquire with until an adviser is
      // engaged. A button that looks live and goes nowhere is worse than one
      // that says what it is waiting for.
      const cta = el("button", "action primary", pathway.action.label);
      cta.type = "button";
      cta.disabled = true;
      cta.title = "Available once Verity's registered advisers are live";
      card.append(cta);
      card.append(
        el(
          "p",
          "pathway-note",
          "Verity's own advisers are not taking cases yet, so this is not live. You do not have to wait for us.",
        ),
      );
    }
    const register = el("a", "action link", "Find a registered adviser");
    register.href = "https://www.gov.uk/find-an-immigration-adviser";
    register.rel = "noopener";
    card.append(register);
  }

  card.append(el("p", "standing-line", STANDING_LINE));
  container.append(card);
}

/**
 * WHERE THE ACTION GOES, and it is not the bottom.
 *
 * A result runs to twenty-odd requirements, each with its own detail. Putting
 * the one thing somebody came to do underneath all of it means they scroll past
 * every rule in Appendix FM to find it, and mostly they do not — "buried in the
 * very long list of requirements details and easily missed", which is exactly
 * what happened.
 *
 * For a SIGNED-IN person the panel goes FIRST, because they are mid-task and
 * the requirements are reference material. For an anonymous visitor it stays
 * where §9.6 puts it, after the result and outside the three-action row: they
 * came to read a result, and leading with an offer would be selling to somebody
 * who has not finished reading.
 */
function placeTakingPanel(
  card: HTMLElement,
  panel: HTMLElement | null,
  signedIn: boolean,
): void {
  if (!panel) return;
  if (signedIn) card.prepend(panel);
  else card.append(panel);
}

export function renderDetermination(
  container: HTMLElement,
  det: Determination,
  ruleset: Ruleset,
  /**
   * Why a signed-in person is here (F5/F6), or undefined for the anonymous
   * visitor this page was built for. Somebody revising is not somebody to
   * offer a sign-up to — they signed up.
   */
  context?: CheckContext,
  existingCaseVersion?: number,
): void {
  container.replaceChildren();

  if (det.verdict === "OUT_OF_SCOPE" && det.out_of_scope) {
    renderOutOfScope(
      container,
      det.out_of_scope.reasons,
      det.out_of_scope.referral_hint,
      det.out_of_scope.flags,
    );
    return;
  }

  const card = el("section", "result-card");
  const applicable = det.requirements.filter((r) => r.status !== "NOT_APPLICABLE");
  const carriers = applicable.filter((r) => VERDICT_CARRIERS.includes(r.rule_id));

  card.append(limits());
  card.append(summary(det, applicable));
  card.append(headline(det, carriers));

  const category = det.requirements.find((r) => r.rule_id === "FIN.CATEGORY");
  if (category?.notes?.[0]) card.append(el("p", "category-line", category.notes[0]));

  for (const group of GROUPS) {
    const inGroup = applicable.filter((r) => r.rule_id.startsWith(group.prefix));
    if (inGroup.length === 0) continue;
    const section = el("section", "group");
    section.append(el("h3", "group-title", group.title));
    for (const requirement of inGroup) section.append(requirementBlock(requirement));
    card.append(section);
  }

  card.append(evidenceSection(applicable));
  card.append(el("p", "standing-line", STANDING_LINE));
  card.append(actions());

  // One or the other, never both: an offer to start is meaningless to somebody
  // who already has a case, and a revision panel is meaningless to a stranger.
  placeTakingPanel(
    card,
    context === undefined ? adviserOffer(det) : revisionPanel(context, existingCaseVersion),
    context !== undefined,
  );
  container.append(card);
  void ruleset;
}

/**
 * What this check does NOT look at, stated BEFORE the result.
 *
 * Putting the limits first is deliberate: a reader who knows the boundary can
 * trust what is inside it. It is also the honest position — the requirements
 * below are checked against answers, not against evidence, and several parts
 * of a real application are not examined here at all.
 */
function limits(): HTMLElement {
  const box = el("aside", "limits");
  box.append(el("h3", "limits-title", "What this check does not look at"));
  const list = el("ul");
  for (const item of CHECK_LIMITS) list.append(el("li", undefined, item));
  box.append(list);
  return box;
}

/**
 * A count of where things stand. CX-6: a gap count NEVER travels alone — the
 * met total always goes with it. No outcome language: this counts
 * requirements, and says nothing about how an application would be decided.
 */
function summary(det: Determination, applicable: RequirementResult[]): HTMLElement {
  const met = applicable.filter((r) => r.status === "MET").length;
  const notMet = applicable.filter((r) => r.status === "NOT_MET").length;
  const pending = applicable.filter((r) => r.status === "PENDING_EVIDENCE").length;
  const judged = applicable.filter((r) => r.status === "ASSESSED_BY_CASEWORKER").length;
  const checkable = met + notMet;

  const wrap = el("div", "summary");
  wrap.append(
    el("p", "summary-count", `${met} of ${checkable} requirements we can check are met on your answers.`),
  );
  const detail: string[] = [];
  if (notMet > 0) detail.push(`${notMet} ${notMet === 1 ? "is" : "are"} not met yet`);
  if (pending > 0) detail.push(`${pending} need${pending === 1 ? "s" : ""} a document to check`);
  if (judged > 0) detail.push(`${judged} ${judged === 1 ? "is" : "are"} judged by a caseworker rather than calculated`);
  if (detail.length > 0) wrap.append(el("p", "summary-detail", `${detail.join(", ")}.`));
  void det;
  return wrap;
}

function headline(det: Determination, carriers: RequirementResult[]): HTMLElement {
  if (det.verdict === "NOT_READY" && det.not_ready) {
    const h = el("h2", "verdict-line attention");
    h.textContent = `On these figures, the financial requirement is first met on ${formatDate(det.not_ready.eligible_from)}.`;
    return h;
  }

  const met = carriers.some((r) => r.status === "MET");
  const notMet = carriers.some((r) => r.status === "NOT_MET");
  const pending = carriers.some((r) => r.status === "PENDING_EVIDENCE");

  if (met && !notMet) {
    const carrier = carriers.find((r) => r.status === "MET");
    const headroom = carrier?.figures?.["headroom"];
    const suffix =
      headroom !== undefined && headroom > 0 ? ` — with ${GBP.format(headroom)} of headroom` : "";
    return el("h2", "verdict-line met", `On these figures, the financial requirement is met${suffix}.`);
  }
  if (notMet) {
    const carrier = carriers.find((r) => r.status === "NOT_MET");
    const headroom = carrier?.figures?.["headroom"];
    const suffix =
      headroom !== undefined && headroom < 0 ? ` — the gap is ${GBP.format(-headroom)}` : "";
    return el(
      "h2",
      "verdict-line attention",
      `On these figures, the financial requirement is not met${suffix}.`,
    );
  }
  if (pending) {
    return el(
      "h2",
      "verdict-line pending",
      "On these figures, more information is needed than this calculator collects.",
    );
  }
  return el("h2", "verdict-line", "Enter your figures to see what the rules ask for.");
}

const FIGURE_LABELS: Record<string, string> = {
  threshold: "Minimum income figure that applies",
  income: "Gross annual salary entered",
  required_savings: "Savings the rules ask for",
  savings_held: "Savings entered",
  headroom: "Headroom",
  shortfall: "Income shortfall covered by savings",
};

function requirementBlock(result: RequirementResult): HTMLElement {
  const block = el("article", `requirement status-${result.status.toLowerCase()}`);

  const header = el("header");
  header.append(el("span", "status-chip", statusLabel(result.status)));
  const cite = el("a", "citation");
  cite.href = result.citation.url;
  cite.rel = "noopener";
  cite.target = "_blank";
  cite.textContent = `${result.citation.instrument} — ${result.citation.paragraph}`;
  header.append(cite);
  block.append(header);

  block.append(el("p", "plain-english", result.plain_english));
  for (const note of result.notes ?? []) block.append(el("p", "note", note));

  if (result.figures) {
    const dl = el("dl", "figures");
    for (const [key, value] of Object.entries(result.figures)) {
      const label = FIGURE_LABELS[key];
      if (!label || key === "floor" || key === "multiplier") continue;
      dl.append(el("dt", undefined, label));
      dl.append(
        el("dd", key === "headroom" && value < 0 ? "attention" : undefined, GBP.format(value)),
      );
    }
    if (dl.childElementCount > 0) block.append(dl);

    // The sum itself, not just its answer. Every tool surveyed shows a
    // verdict and hides the arithmetic, which leaves a reader unable to see
    // why the figure is what it is — or to notice a wrong multiplier.
    const steps = financialWorking(result);
    if (steps.length > 0) {
      const working = el("div", "working");
      working.append(el("p", "working-title", "How that is worked out"));
      for (const step of steps) {
        const row = el("p", "working-step");
        row.append(el("span", "working-expression", step.expression));
        row.append(el("span", "working-equals", "="));
        row.append(el("span", "working-result", step.result));
        working.append(row);
        if (step.note) working.append(el("p", "working-note", step.note));
      }
      block.append(working);
    }
  }

  if (result.eligible_from) {
    block.append(
      el("p", "date-line", `The date this is first satisfied: ${formatDate(result.eligible_from)}.`),
    );
  }
  const catAFrom = result.dates?.["category_a_available_from"];
  if (catAFrom) {
    block.append(
      el("p", "date-line", `Category A becomes available on ${formatDate(catAFrom)}.`),
    );
  }
  return block;
}

function statusLabel(status: RequirementResult["status"]): string {
  switch (status) {
    case "MET":
      return "Met on these figures";
    case "NOT_MET":
      return "Not met on these figures";
    case "PENDING_EVIDENCE":
      return "More information needed";
    case "NOT_APPLICABLE":
      return "Does not apply";
    case "ASSESSED_BY_CASEWORKER":
      return "Judged on your evidence";
  }
}

function evidenceSection(results: RequirementResult[]): HTMLElement {
  const details = el("details", "evidence");
  details.id = "evidence";
  details.append(el("summary", undefined, "See what you would need to prove"));
  const list = el("ul");
  const seen = new Set<string>();
  for (const result of results) {
    for (const spec of result.evidence_required) {
      if (seen.has(spec.id)) continue;
      seen.add(spec.id);
      const item = el("li");
      item.append(el("span", undefined, spec.label));
      for (const rule of spec.format_rules ?? []) item.append(el("small", "format-rule", rule));
      list.append(item);
    }
  }
  if (list.childElementCount === 0) {
    list.append(el("li", undefined, "Nothing further, on the answers given."));
  }
  details.append(list);
  return details;
}

/**
 * The offer at the end of the check: a registered adviser, and a way to start.
 *
 * SHOWN ONLY WHERE THE CHECK DID NOT ROUTE OUT. Someone whose case Verity
 * cannot take must never be offered a service that cannot help them — they get
 * the pathway in `renderOutOfScope` instead, which is the whole point of
 * ADR-0039. A routed-out determination never reaches this function, and the
 * guard is repeated here anyway because the cost of getting it wrong is a
 * person paying for something that was never available to them.
 *
 * IT SITS OUTSIDE THE §9.6 ACTION ROW on purpose. Those three — email it to me,
 * email it to my partner, see what you would need to prove — are things to do
 * with the result you have. This is a different kind of thing, and mixing it in
 * would both bury it and disturb an order the spec fixes.
 *
 * THE COPY IS A REGULATED OFFER and has not been reviewed by an adviser. It
 * states what Verity would do and makes no claim about the application: no
 * likelihood, no prospect, no suggestion that using Verity changes an outcome
 * (§3.1.3, prohibition 2). It also avoids the shape of "based on your
 * circumstances" — the banned phrase exists because personalised framing reads
 * as advice, and the words around it matter as much as the words themselves.
 */
/**
 * What somebody sees when they came back to change an answer (F5).
 *
 * NO CLAIM ABOUT WHAT THE CHANGE MEANS. It says the answers will be saved and
 * that the previous version is kept — both facts about our record-keeping. Why
 * a verdict moved is a statement about the Rules, and those come from the
 * ruleset verbatim or not at all (CLAUDE.md rule 10).
 *
 * "Kept" is worth saying out loud rather than implying: §9.1 exists because
 * somebody may one day need to show what they were told, and a person changing
 * an answer is entitled to know the old one does not vanish.
 */
function revisionPanel(context: CheckContext, version?: number): HTMLElement {
  const setup = context === "setup";
  const section = el("section", "offer");

  section.append(
    el("h3", "offer-title", setup ? "That is the check done" : "Save this to your case"),
  );
  section.append(
    el(
      "p",
      "offer-lede",
      setup
        ? "These answers are what everything on your case is worked out from. Save them and carry on setting up."
        : "These answers replace the ones on your case. The previous version is kept, so what you were told before is still on record.",
    ),
  );
  if (!setup && version !== undefined) {
    section.append(el("p", "offer-note", `Your case is currently at version ${version}.`));
  }

  // "Start your application" to somebody who already started one reads as
  // though the first attempt did not take.
  const save = el(
    "button",
    "action primary offer-start",
    setup ? "Continue set-up" : "Save and go back to my case",
  );
  save.id = "save-revision";
  save.type = "button";
  section.append(save);
  return section;
}

function adviserOffer(det: Determination): HTMLElement | null {
  if (!__ADVISER_OFFER__) return null;
  if (det.verdict === "OUT_OF_SCOPE") return null;

  const section = el("section", "offer");
  section.append(el("h3", "offer-title", "Taking this further"));
  section.append(
    el(
      "p",
      "offer-body",
      "Verity assigns you a registered immigration adviser to prepare your application with you and submit it. They read your evidence before it goes anywhere, and they answer the questions the rules cannot.",
    ),
  );
  section.append(
    el(
      "p",
      "offer-note",
      "Nothing is charged until an adviser has taken your case on. Your answers come with you, so you would not start again.",
    ),
  );

  const start = el("a", "action primary offer-start", "Start your application");
  start.id = "start-application";
  // /app/ is behind authentication, so an unauthenticated visitor is sent to
  // sign-up and returned here afterwards — which is F2's job, not this one's.
  start.href = "/app/";
  section.append(start);
  return section;
}

/**
 * Post-result actions (§9.6 order). "Save this result" is not rendered:
 * saving needs an account, and a permanently disabled button is worse than
 * no button. Emailing yourself the result performs the same capture (§9.3),
 * so the offer is real rather than deferred.
 */
function actions(): HTMLElement {
  const wrap = el("div", "actions-wrap");
  const row = el("div", "actions");

  // "Email it to me" only appears when we do not already have an address.
  // Offering to send a copy that is already on its way reads as though the
  // first one failed. main.ts hides it when the intro captured one.
  const emailMe = el("button", "action primary", "Email it to me");
  emailMe.type = "button";
  emailMe.id = "email-me-button";
  row.append(emailMe);

  const emailPartner = el("button", "action primary", "Email it to my partner");
  emailPartner.type = "button";
  emailPartner.id = "email-partner-button";
  row.append(emailPartner);

  const evidence = el("a", "action link", "See what you would need to prove");
  evidence.href = "#evidence";
  row.append(evidence);

  wrap.append(row);
  wrap.append(emailPanel());
  return wrap;
}

/**
 * One form serves both sends. The partner field is revealed rather than
 * duplicated, so there is a single code path for the request and a single
 * consent record. There is deliberately no free-text message field — every
 * word of the outgoing email is ours (ADR-0017).
 */
function emailPanel(): HTMLElement {
  const panel = el("div", "email-panel");
  panel.id = "email-panel";
  panel.hidden = true;

  const heading = el("h3", "email-heading", "Email this result");
  heading.id = "email-panel-heading";
  heading.tabIndex = -1;
  panel.append(heading);

  panel.append(labelled("email-first-name", "first_name", "text", "Your first name"));
  panel.append(labelled("email-address", "email", "email", "Your email"));

  const partnerField = labelled(
    "email-partner-address",
    "partner_email",
    "email",
    "Your partner's email",
  );
  partnerField.id = "partner-field";
  partnerField.hidden = true;
  panel.append(partnerField);

  const marketing = el("label", "checkbox");
  const box = el("input");
  box.type = "checkbox";
  box.id = "email-marketing";
  box.name = "marketing";
  marketing.append(box);
  marketing.append(
    el("span", undefined, " Tell me if the income threshold behind these figures changes"),
  );
  panel.append(marketing);

  panel.append(el("p", "consent-wording", ""));

  const send = el("button", "action primary", "Send it");
  send.type = "button";
  send.id = "email-send-button";
  panel.append(send);

  const status = el("p", "email-status", "");
  status.id = "email-status";
  status.setAttribute("role", "status");
  panel.append(status);

  return panel;
}

function labelled(id: string, name: string, type: string, labelText: string): HTMLElement {
  const wrap = el("div", "field");
  const label = el("label", undefined, labelText);
  label.htmlFor = id;
  const input = el("input");
  input.type = type;
  input.id = id;
  input.name = name;
  input.autocomplete = type === "email" ? "email" : "given-name";
  wrap.append(label, input);
  return wrap;
}
